import React, { useState, useEffect } from "react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import app, { auth } from "../../../firebaseConfig";
import { deriveCompanyAccessState } from "../../../utils/companyAccess";

const CompanyStatusCard = ({ companyKey: companyKeyProp }) => {
  const firestore = getFirestore(app);
  const [resolvedCompanyKey, setResolvedCompanyKey] = useState(companyKeyProp || "");
  const [companyStatus, setCompanyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Resolve companyKey from prop or current signed-in user's Firestore users/{uid}.companyName
  useEffect(() => {
    let mounted = true;
    
    if (companyKeyProp) {
      setResolvedCompanyKey(companyKeyProp);
      return;
    }
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) {
        if (mounted) setResolvedCompanyKey("");
        return;
      }
      try {
        const userRef = doc(firestore, "users", u.uid);
        const userSnap = await getDoc(userRef);
        const data = userSnap.exists() ? userSnap.data() : null;
        if (mounted) setResolvedCompanyKey(data?.companyName || "");
      } catch (err) {
        console.error("Error resolving companyKey:", err);
        if (mounted) setResolvedCompanyKey("");
      }
    });
    return () => {
      mounted = false;
      unsub();
    };
  }, [companyKeyProp, firestore]);

  useEffect(() => {
    if (!resolvedCompanyKey) {
      setLoading(false);
      return;
    }

    const fetchCompanyStatus = async () => {
      try {
        setLoading(true);
        const candidates = Array.from(
          new Set(
            [resolvedCompanyKey, String(resolvedCompanyKey || "").replace(/\//g, "-")].filter(Boolean)
          )
        );
        let resolved = null;
        for (const candidate of candidates) {
          const companySnap = await getDoc(doc(firestore, "companies", candidate));
          if (!companySnap.exists()) continue;
          const companyData = companySnap.data();
          const subscriptionSnap = await getDoc(
            doc(firestore, "companies", candidate, "subscriptionSnapshots", "current")
          );
          resolved = {
            ...deriveCompanyAccessState({
              ...companyData,
              ...(subscriptionSnap.exists() ? subscriptionSnap.data() : {}),
            }),
            created_at: companyData?.createdAt || companyData?.created_at || null,
          };
          break;
        }
        setCompanyStatus(resolved);
      } catch (err) {
        console.error('Error fetching company status:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyStatus();
  }, [resolvedCompanyKey, firestore]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-xl border border-red-200 p-6">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Status</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!companyStatus) {
    return (
      <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-6">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Subscription Not Configured</h3>
            <p className="text-sm text-yellow-700 mt-1">
              This company was created before the subscription system. Please contact support to activate your subscription.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-800',
          badge: 'bg-green-100 text-green-800',
          icon: 'text-green-400'
        };
      case 'paused':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-800',
          badge: 'bg-yellow-100 text-yellow-800',
          icon: 'text-yellow-400'
        };
      case 'suspended':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-800',
          badge: 'bg-red-100 text-red-800',
          icon: 'text-red-400'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-800',
          badge: 'bg-gray-100 text-gray-800',
          icon: 'text-gray-400'
        };
    }
  };

  const statusColors = getStatusColor(companyStatus.status);
  const isExpiringSoon = companyStatus.days_until_expiry <= 7;
  const isExpired = companyStatus.days_until_expiry === 0 && !companyStatus.is_subscription_active;

  return (
    <div className={`rounded-xl border ${statusColors.border} ${statusColors.bg} p-6 shadow-sm`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className={`w-10 h-10 rounded-full ${statusColors.badge} flex items-center justify-center`}>
            {companyStatus.status === 'active' && (
              <svg className={`w-6 h-6 ${statusColors.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {companyStatus.status === 'paused' && (
              <svg className={`w-6 h-6 ${statusColors.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {companyStatus.status === 'suspended' && (
              <svg className={`w-6 h-6 ${statusColors.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            )}
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold text-gray-900">Company Status</h3>
            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors.badge} mt-1`}>
              {companyStatus.status.charAt(0).toUpperCase() + companyStatus.status.slice(1)}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Subscription Expiry */}
        <div className={`p-4 rounded-lg ${isExpired ? 'bg-red-100' : isExpiringSoon ? 'bg-yellow-100' : 'bg-white'} border ${isExpired ? 'border-red-200' : isExpiringSoon ? 'border-yellow-200' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Subscription Status</p>
              <p className={`text-xs mt-1 ${isExpired ? 'text-red-700' : isExpiringSoon ? 'text-yellow-700' : 'text-gray-600'}`}>
                {isExpired ? 'Expired' : isExpiringSoon ? `Expires in ${companyStatus.days_until_expiry} days` : `${companyStatus.days_until_expiry} days remaining`}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-600">Expires on</p>
              <p className="text-sm font-semibold text-gray-900">
                {new Date(companyStatus.subscription_expires_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Can Operate Status */}
        <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full ${companyStatus.can_operate ? 'bg-green-500' : 'bg-red-500'} mr-3`}></div>
            <span className="text-sm font-medium text-gray-900">Operational Status</span>
          </div>
          <span className={`text-sm font-semibold ${companyStatus.can_operate ? 'text-green-700' : 'text-red-700'}`}>
            {companyStatus.can_operate ? 'Operational' : 'Restricted'}
          </span>
        </div>

        {/* Status Reason (if any) */}
        {companyStatus.status_reason && (
          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <p className="text-xs font-medium text-gray-600 mb-1">Status Note</p>
            <p className="text-sm text-gray-800">{companyStatus.status_reason}</p>
          </div>
        )}

        {/* Warning Messages */}
        {isExpired && (
          <div className="flex items-start p-3 bg-red-100 rounded-lg border border-red-200">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">Your subscription has expired</p>
              <p className="text-xs text-red-700 mt-1">Please renew your subscription to continue using the platform.</p>
            </div>
          </div>
        )}
        {!isExpired && isExpiringSoon && (
          <div className="flex items-start p-3 bg-yellow-100 rounded-lg border border-yellow-200">
            <svg className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-800">Subscription expiring soon</p>
              <p className="text-xs text-yellow-700 mt-1">Your subscription will expire in {companyStatus.days_until_expiry} days. Please renew to avoid service interruption.</p>
            </div>
          </div>
        )}

        {/* Company Created */}
        <div className="text-center pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            {companyStatus.created_at
              ? `Company created on ${new Date(companyStatus.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}`
              : "Company creation date unavailable"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompanyStatusCard;
